import { useCallback, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import i18n, {
  AppLocale,
  BUNDLED_VERSIONS,
  DEFAULT_LOCALE,
  LOCALE_EVENT_NAME,
  LOCALE_STORAGE_KEY,
  LOCALE_USER_CHANGED_EVENT,
  applyServerTranslationOverrides,
  isAdminPath,
  localeDirection,
  normalizeLocale,
} from '../i18n';
import { authService } from '../services/authService';
import {
  fetchPublishedUiTranslationBundle,
  fetchUiTranslationVersion,
} from '../services/uiTranslationService';
import { runWithHeldContentFade } from '../utils/pageCrossfade';

let cachedLocale: AppLocale | null = null;
let serverOverridesLoaded: Partial<Record<AppLocale, boolean>> = {};
let appliedPublishedVersion: Partial<Record<AppLocale, string>> = {};

function readStoredLocale(): AppLocale {
  if (cachedLocale) return cachedLocale;
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored) {
      cachedLocale = normalizeLocale(stored);
      return cachedLocale;
    }
  } catch {
    // ignore
  }
  cachedLocale = DEFAULT_LOCALE;
  return DEFAULT_LOCALE;
}

function persistLocale(locale: AppLocale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    cachedLocale = locale;
  } catch {
    cachedLocale = locale;
  }
}

function applyLocaleToDocument(locale: AppLocale): void {
  const effective = isAdminPath() ? DEFAULT_LOCALE : locale;
  const root = document.documentElement;
  root.lang = effective;
  root.dir = localeDirection(effective);
  root.setAttribute('data-locale', effective);
  root.classList.toggle('locale-fa', effective === 'fa');
  root.classList.toggle('locale-en', effective === 'en');
  document.body.classList.toggle('font-persian', effective === 'fa');
  document.body.classList.toggle('font-english', effective === 'en');
}

/** Force i18next + document to English on admin routes (learner fa preference stays in storage). */
export async function ensureAdminEnglishLocale(): Promise<void> {
  if (!isAdminPath()) return;
  await ensureServerOverrides(DEFAULT_LOCALE);
  if (normalizeLocale(i18n.language) !== DEFAULT_LOCALE) {
    await i18n.changeLanguage(DEFAULT_LOCALE);
  }
  applyLocaleToDocument(readStoredLocale());
}

/** Restore learner locale after leaving admin. */
export async function restoreLearnerLocaleFromStorage(): Promise<void> {
  if (isAdminPath()) return;
  const stored = readStoredLocale();
  await ensureServerOverrides(stored);
  if (normalizeLocale(i18n.language) !== stored) {
    await i18n.changeLanguage(stored);
  }
  applyLocaleToDocument(stored);
}

async function ensureServerOverrides(locale: AppLocale, options?: { force?: boolean }): Promise<void> {
  if (!options?.force && serverOverridesLoaded[locale]) return;
  try {
    const serverVersion = await fetchUiTranslationVersion(locale);
    if (!serverVersion || serverVersion === '0') {
      serverOverridesLoaded[locale] = true;
      return;
    }
    if (!options?.force && serverVersion === BUNDLED_VERSIONS[locale]) {
      appliedPublishedVersion[locale] = serverVersion;
      serverOverridesLoaded[locale] = true;
      return;
    }
    if (!options?.force && appliedPublishedVersion[locale] === serverVersion) {
      serverOverridesLoaded[locale] = true;
      return;
    }
    const entries = await fetchPublishedUiTranslationBundle(locale, serverVersion);
    if (entries && Object.keys(entries).length > 0) {
      applyServerTranslationOverrides(locale, entries);
      appliedPublishedVersion[locale] = serverVersion;
    }
    serverOverridesLoaded[locale] = true;
  } catch {
    // Bundled snapshot from build remains in use
  }
}

function broadcastLocale(locale: AppLocale): void {
  window.dispatchEvent(new CustomEvent(LOCALE_EVENT_NAME, { detail: locale }));
}

/** Shared bootstrap so every `useLocale()` hook does not re-fetch prefs / changeLanguage. */
let localeBootstrapPromise: Promise<AppLocale> | null = null;
/** Drops a toast for an older switch when the learner changes language again mid-fade. */
let localeChangeGeneration = 0;

function bootstrapLocaleOnce(): Promise<AppLocale> {
  if (!localeBootstrapPromise) {
    localeBootstrapPromise = (async () => {
      let initial = readStoredLocale();
      // Learner preference only — admin chrome stays English.
      if (!isAdminPath() && authService.isAuthenticated()) {
        try {
          const prefs = await authService.getLearningPreferences();
          initial = normalizeLocale(prefs.preferredLanguage);
          persistLocale(initial);
        } catch {
          // use localStorage
        }
      }
      const active = isAdminPath() ? DEFAULT_LOCALE : initial;
      await ensureServerOverrides(active);
      await i18n.changeLanguage(active);
      applyLocaleToDocument(initial);
      return initial;
    })();
  }
  return localeBootstrapPromise;
}

export interface UseLocaleResult {
  locale: AppLocale;
  direction: 'ltr' | 'rtl';
  isPersian: boolean;
  setLocale: (next: AppLocale, options?: { persistUser?: boolean; announce?: boolean }) => Promise<void>;
  loading: boolean;
}

export function useLocale(): UseLocaleResult {
  const [locale, setLocaleState] = useState<AppLocale>(() => readStoredLocale());
  const [loading, setLoading] = useState(false);
  const effectiveLocale = isAdminPath() ? DEFAULT_LOCALE : locale;
  const direction = localeDirection(effectiveLocale);

  const changeLocale = useCallback(async (next: AppLocale, options?: { persistUser?: boolean; announce?: boolean }) => {
    const normalized = normalizeLocale(next);
    const announce = options?.announce === true && !isAdminPath();
    setLoading(true);
    const generation = announce ? ++localeChangeGeneration : localeChangeGeneration;
    try {
      if (announce) {
        await ensureServerOverrides(normalized);
        if (generation !== localeChangeGeneration) return;
        await runWithHeldContentFade(async () => {
          if (generation !== localeChangeGeneration) return;
          persistLocale(normalized);
          await i18n.changeLanguage(normalized);
          applyLocaleToDocument(normalized);
          flushSync(() => {
            setLocaleState(normalized);
            broadcastLocale(normalized);
          });
        }, () => {
          if (generation !== localeChangeGeneration || isAdminPath()) return;
          window.dispatchEvent(new CustomEvent(LOCALE_USER_CHANGED_EVENT, { detail: normalized }));
        }, { soft: true });
      } else {
        // Persist learner preference even while visiting admin, but keep admin UI on English.
        persistLocale(normalized);
        setLocaleState(normalized);
        broadcastLocale(normalized);

        if (!isAdminPath()) {
          await ensureServerOverrides(normalized);
          await i18n.changeLanguage(normalized);
          applyLocaleToDocument(normalized);
        } else {
          await ensureAdminEnglishLocale();
        }
      }

      if (options?.persistUser !== false && authService.isAuthenticated()) {
        try {
          const prefs = await authService.getLearningPreferences();
          if (prefs.preferredLanguage !== normalized) {
            await authService.updateLearningPreferences({
              ...prefs,
              preferredLanguage: normalized,
            });
          }
        } catch (err) {
          console.warn('Failed to persist preferred language to profile:', err);
        }
      }
    } finally {
      if (generation === localeChangeGeneration) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        await bootstrapLocaleOnce();
        const current = readStoredLocale();
        setLocaleState(current);
        if (isAdminPath()) {
          await ensureAdminEnglishLocale();
        } else if (normalizeLocale(i18n.language) !== current) {
          await i18n.changeLanguage(current);
          applyLocaleToDocument(current);
        } else {
          applyLocaleToDocument(current);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    applyLocaleToDocument(locale);
    // Keep i18next on English while the URL is an admin route.
    if (isAdminPath() && normalizeLocale(i18n.language) !== DEFAULT_LOCALE) {
      void ensureAdminEnglishLocale();
    }
  });

  useEffect(() => {
    const onLocaleEvent = (event: Event) => {
      const detail = (event as CustomEvent<AppLocale>).detail;
      if (detail && detail !== locale) {
        setLocaleState(normalizeLocale(detail));
      }
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === LOCALE_STORAGE_KEY && event.newValue) {
        setLocaleState(normalizeLocale(event.newValue));
      }
    };
    window.addEventListener(LOCALE_EVENT_NAME, onLocaleEvent as EventListener);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(LOCALE_EVENT_NAME, onLocaleEvent as EventListener);
      window.removeEventListener('storage', onStorage);
    };
  }, [locale]);

  return {
    locale: effectiveLocale,
    direction,
    isPersian: effectiveLocale === 'fa',
    setLocale: changeLocale,
    loading,
  };
}

export function bootstrapLocaleDocument(): void {
  applyLocaleToDocument(readStoredLocale());
}

export function invalidateUiTranslationCache(): void {
  serverOverridesLoaded = {};
  appliedPublishedVersion = {};
  localeBootstrapPromise = null;
}

export async function refreshPublishedUiTranslations(locales: AppLocale[] = ['en', 'fa']): Promise<void> {
  invalidateUiTranslationCache();
  await Promise.all(locales.map((locale) => ensureServerOverrides(locale, { force: true })));
}
